import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminGuard } from '@/components/AdminGuard';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import { api, type UserPublic } from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/users')({
  component: UsersPage,
});

function UsersPage() {
  return (
    <AdminGuard>
      <UsersPageBody />
    </AdminGuard>
  );
}

function UsersPageBody() {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ['users'],
    queryFn: () => api.adminListUsers(),
  });

  if (listQ.isLoading) return <PageLoading />;
  if (listQ.error) return <PageError message={String(listQ.error)} />;
  const users = listQ.data ?? [];

  function onCreated() {
    qc.invalidateQueries({ queryKey: ['users'] });
  }

  return (
    <div className="space-y-8">
      <div>
        <h1>Users</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everyone with a Scribe account. Admins see every user's prompts and runs; regular users see only their own.
        </p>
      </div>

      <section>
        <h2 className="text-base font-medium text-muted-foreground">Add a user</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The new account is created immediately; the operator then signs in at <code className="font-mono">/login</code> with the password you set. Pick role = admin to mint another admin.
        </p>
        <AddUserForm onCreated={onCreated} />
      </section>

      <section>
        <h2 className="text-base font-medium text-muted-foreground">
          Registered accounts ({users.length})
        </h2>
        <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Email</th>
                <th className="px-4 py-2 text-left font-medium">Role</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.email}
                  className="border-t border-border last:border-b-0"
                >
                  <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">
                    {u.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        <ShieldCheck className="size-3" />
                        admin
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">user</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(u.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No users yet. Add one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AddUserForm({ onCreated }: { onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api.adminCreateUser({
        email: email.trim().toLowerCase(),
        password,
        role,
      }),
    onSuccess: (u: UserPublic) => {
      toast.success(`Created ${u.email} (${u.role})`);
      setEmail('');
      setPassword('');
      setRole('user');
      setError(null);
      onCreated();
    },
    onError: (e) => {
      const msg = String(e);
      if (msg.includes('409')) setError('That email is already registered.');
      else setError(msg);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError(null);
    createMut.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 grid gap-3 rounded-lg border border-border bg-card p-4 shadow-sm sm:grid-cols-[2fr_2fr_1fr_auto] sm:items-end"
    >
      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Email
        </label>
        <input
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="alice@example.com"
        />
      </div>
      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Password
        </label>
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="8 characters minimum"
        />
      </div>
      <div>
        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Role
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
          className="mt-1 h-9 w-full rounded-md border border-input bg-card px-2 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="submit"
          disabled={createMut.isPending}
          className={cn(
            'inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60',
          )}
        >
          {createMut.isPending ? (
            <Spinner className="size-4 text-primary-foreground" />
          ) : (
            <UserPlus className="size-4" />
          )}
          Add user
        </button>
      </div>
      {error && (
        <div className="sm:col-span-4 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-sm text-destructive">
          {error}
        </div>
      )}
    </form>
  );
}
